import { shipmentTypeLabels } from '@entities/fulfillment'
import { messengers, type FulfillmentRequestApi } from '@entities/fulfillment-request'
import { faEnvelope, faPhone } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Badge } from '@ui/base/badge'
import { typeClass } from '@utils/typography'

import { countLabel, dateLabel } from '../lib/format'

const messengerLabel = (code: FulfillmentRequestApi.Messenger) =>
  messengers.find((messenger) => messenger.code === code)?.label ?? code

/** Главные условия заявки: по ним фулфилмент считает цену, поэтому они крупные и отдельными плитками. */
const facts = (request: FulfillmentRequestApi.FulfillmentRequest) => [
  { label: 'Количество', value: request.unit_count === null ? '—' : `${countLabel(request.unit_count)} ед.` },
  { label: 'Нужно до', value: dateLabel(request.due_date) ?? 'срок не указан' },
  {
    label: 'Забрать в городе',
    value: request.cities.length > 0 ? request.cities.map((city) => city.name).join(', ') : 'любой город',
  },
  {
    label: 'Схемы работы',
    value: request.shipment_types.map((type) => shipmentTypeLabels[type]).join(', ') || '—',
  },
  { label: 'Категория', value: request.subject_name ?? 'не указана' },
]

/** Подробности, которые нужны не всегда: показываем только заполненные строки. */
const details = (request: FulfillmentRequestApi.FulfillmentRequest) =>
  [
    { label: 'Склады отгрузки', value: request.warehouses.map((warehouse) => warehouse.name).join(', ') },
    { label: 'Габариты товара', value: request.product_size },
    { label: 'Габариты упаковки', value: request.package_size },
    {
      label: 'Во что паковать',
      value: [request.packing_type_label, request.packing_comment].filter(Boolean).join(': '),
    },
    { label: 'Особая упаковка', value: request.special_packing },
    { label: 'Особые требования', value: request.requirements },
  ].filter((detail): detail is { label: string; value: string } => Boolean(detail.value))

export const RequestFacts = ({ request }: { request: FulfillmentRequestApi.FulfillmentRequest }) => {
  const filledDetails = details(request)
  const federalDistricts = request.federal_districts ?? []

  return (
    <div className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {facts(request).map((fact) => (
          <div key={fact.label} className="grid gap-1 rounded-lg border p-3">
            <span className={`${typeClass.caption} text-muted-foreground`}>{fact.label}</span>
            <span className={typeClass.pageTitle}>{fact.value}</span>
          </div>
        ))}
      </div>

      {federalDistricts.length > 0 && (
        <div className="grid gap-2">
          <span className={typeClass.label}>Куда везти</span>
          <div className="flex flex-wrap gap-1.5">
            {federalDistricts.map((district) => (
              <Badge key={district.code} variant="secondary">
                {district.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {request.services.length > 0 && (
        <div className="grid gap-2">
          <span className={typeClass.label}>Нужные услуги</span>
          <div className="flex flex-wrap gap-1.5">
            {request.services.map((service) => (
              <Badge key={service.code} variant="secondary">
                {service.title}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {request.needs_twice_a_day && <Badge variant="warning">Нужны две отгрузки в день</Badge>}

      {filledDetails.length > 0 && (
        <dl className="grid gap-2 sm:grid-cols-2">
          {filledDetails.map((detail) => (
            <div key={detail.label} className="grid gap-0.5">
              <dt className={`${typeClass.caption} text-muted-foreground`}>{detail.label}</dt>
              <dd className={typeClass.body}>{detail.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

/**
 * Контакты продавца. Телефон и мессенджеры бэкенд отдаёт только тем фулфилментам, которым
 * заявка адресована, поэтому блок появляется не всегда.
 */
export const RequestContacts = ({ request }: { request: FulfillmentRequestApi.FulfillmentRequest }) => {
  if (!request.phone) {
    return null
  }

  const chosen = request.contacts ?? []

  return (
    <div className="grid gap-2 rounded-lg border border-primary/40 bg-primary/5 p-4">
      <span className={typeClass.sectionTitle}>Контакты продавца</span>
      <a
        href={`tel:${request.phone.replace(/[^\d+]/g, '')}`}
        className={`${typeClass.pageTitle} flex items-center gap-2`}
      >
        <FontAwesomeIcon icon={faPhone} className="text-muted-foreground" />
        {request.phone}
      </a>
      <span className={`${typeClass.supporting} text-muted-foreground flex items-center gap-2`}>
        <FontAwesomeIcon icon={faEnvelope} />
        {chosen.length > 0
          ? chosen.map((contact) => `${messengerLabel(contact.messenger)}: ${contact.value}`).join(' · ')
          : 'Мессенджеры продавец не указал'}
      </span>
    </div>
  )
}
